import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  executeAction,
  publishUnreadCount,
} from '../../services/notificationService';

/**
 * useNotifications – central hook for the Notifications Inbox.
 *
 * Provides:
 *  - items[], meta, loading, hasMore
 *  - loadMore()           (infinite scroll)
 *  - refresh()            (resets to page 1)
 *  - handleMarkRead(id)
 *  - handleMarkAllRead()
 *  - handleDelete(id)
 *  - unreadCount          (badge number, refreshed while the tab is visible)
 *  - filter / setFilter   ('all' | 'unread')
 *  - category / setCategory
 */
export default function useNotifications() {
  const { isAuthenticated } = useAuth();

  /* ── State ───────────────────────────────── */
  const [items, setItems]           = useState([]);
  const [meta, setMeta]             = useState({});
  const [loading, setLoading]       = useState(false);
  const [unreadCount, setUnread]    = useState(0);
  const [filter, setFilter]         = useState('all');      // 'all' | 'unread'
  const [category, setCategory]     = useState('');         // '' = all categories
  const [loadedQueryKey, setLoadedQueryKey] = useState(null);
  const queryKey = `${filter}:${category}`;
  const pageRef   = useRef(1);
  const hasMore   = useRef(true);
  const mountedRef = useRef(true);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    publishUnreadCount(unreadCount);
  }, [unreadCount]);

  /* ── Fetch a page ────────────────────────── */
  const fetchPage = useCallback(async (page, append = false) => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const params = { page, limit: 20, filter };
      if (category) params.category = category;

      const result = await getNotifications(params);
      if (!mountedRef.current) return;

      const newItems  = result.items || [];
      const pageMeta  = result.meta  || {};

      setItems(prev => append ? [...prev, ...newItems] : newItems);
      setMeta(pageMeta);
      hasMore.current = page < (pageMeta.totalPages || 1);
    } catch (err) {
      console.error('[useNotifications] fetch error', err);
    } finally {
      if (mountedRef.current) {
        if (page === 1) setLoadedQueryKey(queryKey);
        setLoading(false);
      }
    }
  }, [isAuthenticated, filter, category, queryKey]);

  /* ── Initial load + react to filter/category changes ── */
  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      setUnread(0);
      setLoadedQueryKey(null);
      setLoading(false);
      return;
    }
    pageRef.current = 1;
    hasMore.current = true;
    fetchPage(1);
  }, [fetchPage, isAuthenticated]);

  /* ── Unread count (initial) ──────────────── */
  useEffect(() => {
    if (!isAuthenticated) return;
    getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
  }, [isAuthenticated]);

  /* ── Visible-tab polling ──────────────────── */
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const poll = () => {
      if (document.visibilityState !== 'visible') return;
      getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
      if (pageRef.current === 1) fetchPage(1);
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisibility);
    const timer = setInterval(poll, 30_000);
    return () => { document.removeEventListener('visibilitychange', onVisibility); clearInterval(timer); };
  }, [fetchPage, isAuthenticated]);

  /* ── Actions ─────────────────────────────── */
  const loadMore = useCallback(() => {
    if (loading || !hasMore.current) return;
    pageRef.current += 1;
    fetchPage(pageRef.current, true);
  }, [loading, fetchPage]);

  const refresh = useCallback(() => {
    pageRef.current = 1;
    hasMore.current = true;
    fetchPage(1);
    getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
  }, [fetchPage]);

  const handleMarkRead = useCallback(async (id) => {
    try {
      await markAsRead(id);
      setItems(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      const freshCount = await getUnreadCount();
      if (mountedRef.current) setUnread(freshCount);
    } catch (err) {
      if (err?.response?.status === 404) {
        setItems(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      }
      getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllAsRead();
      setItems(prev => prev.map(n => ({ ...n, isRead: true })));
      const freshCount = await getUnreadCount();
      if (mountedRef.current) setUnread(freshCount);
    } catch {
      getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    try {
      await deleteNotification(id);
      setItems(prev => {
        const removed = prev.find(n => n.id === id);
        if (removed && !removed.isRead) setUnread(p => Math.max(0, p - 1));
        return prev.filter(n => n.id !== id);
      });
    } catch { /* silent */ }
  }, []);

  /**
   * Execute a dynamic notification action instance.
   * Updates the action's status in the local items list.
   * @param {number} notificationId
   * @param {number} instanceId
   * @param {object} [payload] - Optional payload (e.g. { reason } for appeal)
   */
  const handleExecuteAction = useCallback(async (notificationId, instanceId, payload) => {
    try {
      const result = await executeAction(instanceId, payload);
      // Update the local actions array for this notification
      setItems(prev => prev.map(n => {
        if (n.id !== notificationId || !n.actions) return n;
        return {
          ...n,
          isRead: true,
          linkUrl: result?.navigateTo || n.linkUrl || n.link_url || null,
          actions: n.actions.map(a => {
            if (a.id === instanceId) return { ...a, status: 'COMPLETED' };
            // Handle mutual exclusion — if the executed action overrides siblings
            if (a.status === 'AVAILABLE' && result.actionCode) return { ...a, status: 'OVERRIDDEN' };
            return a;
          }),
        };
      }));
      setUnread(prev => Math.max(0, prev - 1));
      return result;
    } catch (err) {
      throw err;
    }
  }, []);

  return {
    items,
    meta,
    loading: loading || Boolean(isAuthenticated && loadedQueryKey !== queryKey),
    hasMore: hasMore.current,
    unreadCount,
    filter,
    setFilter,
    category,
    setCategory,
    loadMore,
    refresh,
    handleMarkRead,
    handleMarkAllRead,
    handleDelete,
    handleExecuteAction,
    setUnreadCount: setUnread,
  };
}
